import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Upload, Link, X, Check } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from "uuid";

interface RichContent {
  enabled: boolean;
  content_type: 'markdown' | 'html' | 'plain';
  media_elements?: Array<{
    type: 'image' | 'link' | 'video';
    url: string;
    alt_text?: string;
    display_text?: string;
    link_url?: string; // For clickable images
    width?: number;
    height?: number;
    position?: 'inline' | 'block' | 'float-left' | 'float-right';
  }>;
}

interface QuestionTextInputProps {
  questionId: string;
  initialText: string;
  onSave: (text: string, richContent?: RichContent) => void;
  placeholder?: string;
  enableRichMedia?: boolean;
  initialRichContent?: RichContent;
  chatbotId?: string;
}

export const QuestionTextInput = ({
  questionId,
  initialText,
  onSave,
  placeholder = "Question text",
  enableRichMedia = false,
  initialRichContent,
  chatbotId
}: QuestionTextInputProps) => {
  // Local state for the text value
  const [text, setText] = useState(initialText);

  // Rich media state
  const [richContent, setRichContent] = useState<RichContent>(initialRichContent || {
    enabled: false,
    content_type: 'plain' as const,
    media_elements: []
  });

  // Track if the input is being edited
  const isEditingRef = useRef(false);

  // Track if this is the initial render
  const initialRenderRef = useRef(true);

  // Update local state when props change, but only if we're not editing
  useEffect(() => {
    if (!isEditingRef.current && !initialRenderRef.current) {
      setText(initialText);
    }
    initialRenderRef.current = false;
  }, [initialText]);

  const handleBlur = () => {
    if (isEditingRef.current) {
      onSave(text, richContent);
      isEditingRef.current = false;
    }
  };

  const addMediaElement = (type: 'image' | 'link' | 'video') => {
    const newElement = {
      type,
      url: '',
      alt_text: type === 'image' ? 'Image' : undefined,
      display_text: type === 'link' ? 'Link' : undefined,
      position: 'block' as const
    };

    const updatedRichContent = {
      ...richContent,
      enabled: true,
      media_elements: [...(richContent.media_elements || []), newElement]
    };

    setRichContent(updatedRichContent);

    // Add placeholder in text
    const placeholder = `{{media_${richContent.media_elements?.length || 0}}}`;
    const updatedText = text + (text ? '\n' : '') + placeholder;
    setText(updatedText);

    // Auto-save when media element is added
    onSave(updatedText, updatedRichContent);
  };

  const toggleRichMedia = () => {
    const newEnabled = !richContent.enabled;
    const updatedRichContent: RichContent = {
      ...richContent,
      enabled: newEnabled,
      content_type: newEnabled ? 'markdown' as const : 'plain' as const
    };

    setRichContent(updatedRichContent);

    // Auto-save when rich media is toggled
    onSave(text, updatedRichContent);
  };

  const updateMediaElement = (index: number, property: string, value: any) => {
    const updatedElements = [...richContent.media_elements];
    updatedElements[index] = {
      ...updatedElements[index],
      [property]: value
    };

    setRichContent(prev => ({
      ...prev,
      media_elements: updatedElements
    }));
  };

  // Handle blur for media element inputs
  const handleMediaElementBlur = () => {
    if (isEditingRef.current) {
      onSave(text, richContent);
      isEditingRef.current = false;
    }
  };

  // Handle key down for media element inputs
  const handleMediaElementKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  const removeMediaElement = (index: number) => {
    const updatedElements = richContent.media_elements.filter((_: any, i: number) => i !== index);
    const updatedRichContent = {
      ...richContent,
      media_elements: updatedElements
    };

    setRichContent(updatedRichContent);

    // Auto-save when media element is removed
    onSave(text, updatedRichContent);
  };

  const moveMediaElement = (index: number, direction: number) => {
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < richContent.media_elements.length) {
      const updatedElements = [...richContent.media_elements];
      const temp = updatedElements[index];
      updatedElements[index] = updatedElements[newIndex];
      updatedElements[newIndex] = temp;

      const updatedRichContent = {
        ...richContent,
        media_elements: updatedElements
      };

      setRichContent(updatedRichContent);

      // Auto-save when media elements are reordered
      onSave(text, updatedRichContent);
    }
  };

  const [imageUploadMode, setImageUploadMode] = useState<{ [key: number]: boolean }>({});
  const [selectedFile, setSelectedFile] = useState<{ [key: number]: File | null }>({});
  const { toast } = useToast();

  const handleImageUpload = async (elementIndex: number) => {
    const file = selectedFile[elementIndex];
    if (!file) {
      toast({
        title: "Error",
        description: "Please select a file to upload",
        variant: "destructive",
      });
      return;
    }

    if (!chatbotId) {
      toast({
        title: "Error",
        description: "Chatbot ID is required for file upload",
        variant: "destructive",
      });
      return;
    }

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `question-media-${uuidv4()}.${fileExt}`;
      const filePath = `${chatbotId}/${fileName}`;

      const { data, error } = await supabase.storage
        .from('chatbot-assets')
        .upload(filePath, file, {
          upsert: true
        });

      if (error) {
        throw error;
      }

      const { data: publicUrlData } = supabase.storage
        .from('chatbot-assets')
        .getPublicUrl(filePath);

      // Update the media element with the uploaded file URL
      updateMediaElement(elementIndex, 'url', publicUrlData.publicUrl);
      
      // Save the updated rich content to the database
      const updatedElements = [...richContent.media_elements];
      updatedElements[elementIndex] = {
        ...updatedElements[elementIndex],
        url: publicUrlData.publicUrl
      };
      
      const updatedRichContent = {
        ...richContent,
        media_elements: updatedElements
      };
      
      onSave(text, updatedRichContent);
      
      // Clear selected file and toggle back to URL mode
      setSelectedFile(prev => ({ ...prev, [elementIndex]: null }));
      setImageUploadMode(prev => ({ ...prev, [elementIndex]: false }));

      toast({
        title: "Success",
        description: "Image uploaded successfully",
      });

    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Error", 
        description: "Failed to upload image",
        variant: "destructive",
      });
    }
  };

  const handleImageUploadToggle = (elementIndex: number) => {
    setImageUploadMode(prev => ({ 
      ...prev, 
      [elementIndex]: !prev[elementIndex] 
    }));
  };

  return (
    <div className="space-y-3">
      {/* Rich Media Toggle */}
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Question Text</label>
        <div className="flex items-center space-x-2">
          <label className="text-xs text-gray-600">Rich Media</label>
          <input
            type="checkbox"
            checked={richContent.enabled}
            onChange={toggleRichMedia}
            className="rounded"
          />
        </div>
      </div>

      {/* Text Input */}
      <textarea
        id={`question-text-${questionId}`}
        value={text}
        onChange={(e) => {
          isEditingRef.current = true;
          setText(e.target.value);
        }}
        onFocus={() => { isEditingRef.current = true; }}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.ctrlKey) {
            e.currentTarget.blur();
          }
        }}
        className="w-full p-3 border rounded-md resize-vertical min-h-[80px]"
        placeholder={richContent.enabled ?
          "Enter your question text. Use markdown formatting or add media elements below." :
          placeholder
        }
      />

      {/* Rich Media Controls */}
      {richContent.enabled && (
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Add media:</span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => addMediaElement('image')}
              className="text-xs"
            >
              📷 Image
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => addMediaElement('link')}
              className="text-xs"
            >
              🔗 Link
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => addMediaElement('video')}
              className="text-xs"
            >
              🎥 Video
            </Button>
          </div>

          <div className="text-xs text-gray-500">
            <p><strong>Markdown support:</strong> **bold**, *italic*, [link text](url), ![alt text](image-url)</p>
            <p><strong>Media placeholders:</strong> Use {`{{media_0}}`}, {`{{media_1}}`}, etc. to position media elements</p>
          </div>

          {/* Media Elements List */}
          {richContent.media_elements && richContent.media_elements.length > 0 && (
            <div className="space-y-3 mt-4">
              <h4 className="text-sm font-medium text-gray-700">Media Elements</h4>
              {richContent.media_elements.map((element: any, index: number) => (
                <div key={index} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      📍 Media {index} ({element.type.toUpperCase()})
                    </span>
                    <div className="flex space-x-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => moveMediaElement(index, -1)}
                        disabled={index === 0}
                        className="text-xs px-2 py-1"
                      >
                        ⬆️
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => moveMediaElement(index, 1)}
                        disabled={index === richContent.media_elements.length - 1}
                        className="text-xs px-2 py-1"
                      >
                        ⬇️
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        onClick={() => removeMediaElement(index)}
                        className="text-xs px-2 py-1"
                      >
                        🗑️
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {/* URL Input */}
                    {element.type === 'image' && (
                      <div>
                        <label className="text-xs text-gray-600">Image URL</label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={element.url || ''}
                            onChange={(e) => {
                              isEditingRef.current = true;
                              updateMediaElement(index, 'url', e.target.value);
                            }}
                            onFocus={() => { isEditingRef.current = true; }}
                            onBlur={handleMediaElementBlur}
                            onKeyDown={handleMediaElementKeyDown}
                            placeholder="Enter image URL"
                            className="w-full p-2 text-sm border rounded-md"
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => handleImageUploadToggle(index)}
                            className="text-xs"
                          >
                            {imageUploadMode[index] ? 'Use URL' : 'Upload Image'}
                          </Button>
                          {imageUploadMode[index] && (
                            <div className="mt-2">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => setSelectedFile(prev => ({ ...prev, [index]: e.target.files[0] }))}
                                className="w-full p-2 text-sm border rounded-md"
                              />
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => handleImageUpload(index)}
                                className="text-xs mt-2"
                              >
                                Upload Image
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {element.type !== 'image' && (
                      <div>
                        <label className="text-xs text-gray-600">URL</label>
                        <input
                          type="text"
                          value={element.url || ''}
                          onChange={(e) => {
                            isEditingRef.current = true;
                            updateMediaElement(index, 'url', e.target.value);
                          }}
                          onFocus={() => { isEditingRef.current = true; }}
                          onBlur={handleMediaElementBlur}
                          onKeyDown={handleMediaElementKeyDown}
                          placeholder={`Enter ${element.type} URL`}
                          className="w-full p-2 text-sm border rounded-md"
                        />
                      </div>
                    )}

                    {/* Type-specific inputs */}
                    {element.type === 'image' && (
                      <>
                        <div>
                          <label className="text-xs text-gray-600">Alt Text</label>
                          <input
                            type="text"
                            value={element.alt_text || ''}
                            onChange={(e) => {
                              isEditingRef.current = true;
                              updateMediaElement(index, 'alt_text', e.target.value);
                            }}
                            onFocus={() => { isEditingRef.current = true; }}
                            onBlur={handleMediaElementBlur}
                            onKeyDown={handleMediaElementKeyDown}
                            placeholder="Alt text for accessibility"
                            className="w-full p-2 text-sm border rounded-md"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600">Link URL (optional)</label>
                          <input
                            type="url"
                            value={element.link_url || ''}
                            onChange={(e) => {
                              isEditingRef.current = true;
                              updateMediaElement(index, 'link_url', e.target.value);
                            }}
                            onFocus={() => { isEditingRef.current = true; }}
                            onBlur={handleMediaElementBlur}
                            onKeyDown={handleMediaElementKeyDown}
                            placeholder="URL to open when image is clicked (optional)"
                            className="w-full p-2 text-sm border rounded-md"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-gray-600">Width (px)</label>
                            <input
                              type="number"
                              value={element.width || ''}
                              onChange={(e) => {
                                isEditingRef.current = true;
                                updateMediaElement(index, 'width', parseInt(e.target.value) || undefined);
                              }}
                              onFocus={() => { isEditingRef.current = true; }}
                              onBlur={handleMediaElementBlur}
                              onKeyDown={handleMediaElementKeyDown}
                              placeholder="Width"
                              className="w-full p-2 text-sm border rounded-md"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600">Height (px)</label>
                            <input
                              type="number"
                              value={element.height || ''}
                              onChange={(e) => {
                                isEditingRef.current = true;
                                updateMediaElement(index, 'height', parseInt(e.target.value) || undefined);
                              }}
                              onFocus={() => { isEditingRef.current = true; }}
                              onBlur={handleMediaElementBlur}
                              onKeyDown={handleMediaElementKeyDown}
                              placeholder="Height"
                              className="w-full p-2 text-sm border rounded-md"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {element.type === 'link' && (
                      <div>
                        <label className="text-xs text-gray-600">Display Text</label>
                        <input
                          type="text"
                          value={element.display_text || ''}
                          onChange={(e) => {
                            isEditingRef.current = true;
                            updateMediaElement(index, 'display_text', e.target.value);
                          }}
                          onFocus={() => { isEditingRef.current = true; }}
                          onBlur={handleMediaElementBlur}
                          onKeyDown={handleMediaElementKeyDown}
                          placeholder="Text to display for the link"
                          className="w-full p-2 text-sm border rounded-md"
                        />
                      </div>
                    )}

                    {element.type === 'video' && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-gray-600">Width (px)</label>
                          <input
                            type="number"
                            value={element.width || ''}
                            onChange={(e) => {
                              isEditingRef.current = true;
                              updateMediaElement(index, 'width', parseInt(e.target.value) || undefined);
                            }}
                            onFocus={() => { isEditingRef.current = true; }}
                            onBlur={handleMediaElementBlur}
                            onKeyDown={handleMediaElementKeyDown}
                            placeholder="Width"
                            className="w-full p-2 text-sm border rounded-md"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600">Height (px)</label>
                          <input
                            type="number"
                            value={element.height || ''}
                            onChange={(e) => {
                              isEditingRef.current = true;
                              updateMediaElement(index, 'height', parseInt(e.target.value) || undefined);
                            }}
                            onFocus={() => { isEditingRef.current = true; }}
                            onBlur={handleMediaElementBlur}
                            onKeyDown={handleMediaElementKeyDown}
                            placeholder="Height"
                            className="w-full p-2 text-sm border rounded-md"
                          />
                        </div>
                      </div>
                    )}

                    {/* Position selector */}
                    <div>
                      <label className="text-xs text-gray-600">Position</label>
                      <select
                        value={element.position || 'block'}
                        onChange={(e) => {
                          isEditingRef.current = true;
                          updateMediaElement(index, 'position', e.target.value);
                        }}
                        onFocus={() => { isEditingRef.current = true; }}
                        onBlur={handleMediaElementBlur}
                        className="w-full p-2 text-sm border rounded-md"
                      >
                        <option value="block">Block (full width)</option>
                        <option value="inline">Inline</option>
                        <option value="float-left">Float Left</option>
                        <option value="float-right">Float Right</option>
                      </select>
                    </div>

                    <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded">
                      <strong>Placeholder:</strong> <code>{`{{media_${index}}}`}</code>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
